import { KpiCategoryFilterConfiguration, KpiCategoryFilterValue } from 'models/request';

export const getFilterValues = (value: any): string[] => (Array.isArray(value) ? value.map((v) => v?.value ?? v) : []);

export const getChildFilterCode = (config: KpiCategoryFilterConfiguration[], filterCode: string): string | undefined =>
  config.find((f) => f.filterCode === filterCode)?.subFilterLink;

export const cleanChildValues = (childValues: any[], parentValues: string[]): any[] => {
  if (parentValues.length === 0) return [];
  return childValues.filter((v) => {
    const value = v?.value ?? v;
    return parentValues.some((parent) => value.startsWith(`${parent}-`));
  });
};

export const cleanDescendants = (
  config: KpiCategoryFilterConfiguration[],
  next: Record<string, any>,
  parentCode: string,
): void => {
  const childCode = getChildFilterCode(config, parentCode);
  if (!childCode) return;

  const parentValues = getFilterValues(next[parentCode]);
  const childValues = next[childCode];

  if (Array.isArray(childValues)) {
    next[childCode] = cleanChildValues(childValues, parentValues);
  }

  cleanDescendants(config, next, childCode);
};

export const buildDefaults = (config: KpiCategoryFilterConfiguration[]): Record<string, any> => {
  const defaults: Record<string, any> = {};
  config.forEach((filter) => {
    if (filter.defaultValue) defaults[filter.filterCode] = filter.defaultValue;
  });
  return defaults;
};

export const buildFilterValues = (
  config: KpiCategoryFilterConfiguration[],
  values: Record<string, any>,
): KpiCategoryFilterValue[] =>
  config
    .map((filter) => {
      const val = values[filter.filterCode];

      if (filter.filterType === 'selectOptions' && Array.isArray(val) && val.length > 0) {
        return {
          filterCode: filter.filterCode,
          multipleValues: val.map((v) => v.value),
        };
      }
      if (filter.filterType === 'dateRange' && Array.isArray(val) && val[0] && val[1]) {
        return { filterCode: filter.filterCode, rangeStartValue: val[0], rangeEndValue: val[1] };
      }
      if (filter.filterType === 'text' && val) {
        return { filterCode: filter.filterCode, singleValue: val };
      }
      return null;
    })
    .filter((f) => f !== null) as KpiCategoryFilterValue[];

export const hasMandatoryFieldsFilled = (
  config: KpiCategoryFilterConfiguration[],
  values: Record<string, any>,
): boolean =>
  config
    .filter((filter) => filter.mandatory)
    .every((filter) => {
      const val = values[filter.filterCode];
      if (filter.filterType === 'selectOptions') return Array.isArray(val) && val.length > 0;
      if (filter.filterType === 'dateRange') return Array.isArray(val) && val[0] && val[1];
      return val !== undefined && val !== null && val !== '';
    });
